import { Plug, ArrowUpRight } from 'lucide-react';
import AtsMarketplace from './AtsMarketplace';

/**
 * Settings panel for the Kombo (kombo.dev) ATS connector. Renders the
 * disconnected state — a status banner + a marketplace of supported ATS
 * providers — and matches the visual language of IdibuSettingsPanel.
 *
 * The "Connect Kombo" CTA is intentionally disabled: the integration
 * isn't yet provisioned. When it lands, swap the disabled handler for
 * the real OAuth/connect flow.
 */
export default function KomboSettingsPanel() {
    return (
        <>
            <style>{PANEL_CSS}</style>

            <div className="km-card-shell km-status">
                <div className="km-status__icon" aria-hidden>
                    <Plug size={20} />
                </div>
                <div className="km-status__copy">
                    <strong>No ATS connected</strong>
                    <span>
                        Connect to sync candidates, roles, and pipeline stages from your existing ATS.
                    </span>
                </div>
                <div className="km-status__actions">
                    <button
                        type="button"
                        className="km-btn km-btn--primary"
                        disabled
                        title="Connecting an ATS isn't available in this build yet"
                    >
                        Connect ATS
                        <ArrowUpRight size={13} />
                    </button>
                </div>
            </div>

            <AtsMarketplace />
        </>
    );
}

const PANEL_CSS = `
.km-card-shell {
    background: var(--surface-primary);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    padding: 16px 18px;
    margin-bottom: 18px;
}

.km-status {
    display: flex;
    align-items: center;
    gap: 14px;
}

.km-status__icon {
    display: grid;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 999px;
    background: var(--surface-tertiary);
    color: var(--text-tertiary);
}

.km-status__copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

.km-status__copy strong {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.005em;
}

.km-status__copy > span {
    color: var(--text-secondary);
    font-size: 12.5px;
    line-height: 1.5;
}

.km-status__copy a {
    color: var(--accent-strong);
    font-weight: 500;
    border-bottom: 1px dashed currentColor;
    padding-bottom: 0;
}

.km-status__copy a:hover {
    color: var(--accent);
}

.km-status__actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
}

.km-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border-radius: var(--radius-md);
    font-size: 12.5px;
    font-weight: 500;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease, color 100ms ease;
    white-space: nowrap;
}

.km-btn:disabled { cursor: not-allowed; opacity: 0.55; }

.km-btn--primary {
    background: var(--text-primary);
    color: var(--text-inverse);
}

.km-btn--primary:hover:not(:disabled) { background: #111418; }

@media (max-width: 720px) {
    .km-status {
        flex-direction: column;
        align-items: stretch;
    }
    .km-status__actions { justify-content: flex-end; }
}
`;
